import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listProjects, getGithubStatus } from "../lib/api";
import AppShell from "../components/AppShell";
import { ArrowRight, Plus, Folder, Loader2 } from "lucide-react";
import { fmtRelative } from "../lib/format";

export default function Dashboard() {
  const [projects, setProjects] = useState(null);
  const [status, setStatus] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([listProjects(), getGithubStatus()]).then(([p, s]) => {
      setProjects(p);
      setStatus(s);
    });
  }, []);

  if (projects === null) {
    return (
      <AppShell>
        <div className="p-12 text-[#6b7280] flex items-center gap-2">
          <Loader2 className="animate-spin" size={16} /> Loading…
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="flex items-end justify-between gap-4 mb-10">
          <div>
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#6b7280]">
              Project Memory
            </span>
            <h1 className="text-4xl sm:text-5xl tracking-tight text-[#111827] mt-2 leading-tight">
              Welcome back. <br />
              <span className="gradient-text">Pick up where you left off.</span>
            </h1>
            <p className="text-[#6b7280] mt-3 max-w-xl">
              Trace remembers your sessions, files and intent — so the next session starts with
              clarity, not guessing.
            </p>
          </div>
          <button
            data-testid="dashboard-new-project"
            onClick={() => navigate("/onboarding")}
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#111827] text-white text-sm hover:bg-[#374151] transition"
          >
            <Plus size={14} /> New project
          </button>
        </div>

        {!status?.connected && (
          <div className="trace-card p-5 mb-8 flex items-center justify-between" data-testid="connect-banner">
            <div>
              <div className="text-sm font-medium text-[#111827]">Connect GitHub to start</div>
              <div className="text-xs text-[#6b7280]">
                Trace needs a Personal Access Token to read your repo activity.
              </div>
            </div>
            <button
              onClick={() => navigate("/onboarding")}
              className="px-4 py-2 rounded-md bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-sm hover:opacity-90 transition"
            >
              Connect
            </button>
          </div>
        )}

        {projects.length === 0 ? (
          <div className="trace-card p-10 text-center" data-testid="dashboard-empty">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/d51a684d-6a21-4b39-8b43-c02965a25ab0/images/b43c5a12eb20faf926312a6d16f17709c71094203a4eb21b99ea782053ea20f3.png"
              alt=""
              className="w-28 h-28 mx-auto opacity-90"
            />
            <h2 className="text-2xl tracking-tight text-[#111827] mt-2">
              No projects in memory yet.
            </h2>
            <p className="text-sm text-[#6b7280] max-w-md mx-auto mt-2">
              Connect a repo to start tracing how you build it.
            </p>
            <button
              onClick={() => navigate("/onboarding")}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#111827] text-white text-sm hover:bg-[#374151] transition"
            >
              <Plus size={14} /> Connect a repo
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="projects-grid">
            {projects.map((p) => (
              <button
                key={p.id}
                data-testid={`project-card-${p.id}`}
                onClick={() => navigate(`/project/${p.id}`)}
                className="text-left trace-card p-6 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Folder size={16} className="text-[#6b7280]" />
                    <span className="text-base font-medium text-[#111827]">{p.name}</span>
                  </div>
                  <ArrowRight size={14} className="text-[#9ca3af]" />
                </div>
                <div className="text-xs text-[#6b7280] mt-1 font-mono truncate">{p.full_name}</div>
                <div className="grid grid-cols-3 gap-2 mt-5">
                  <Mini label="Sessions" value={p.sessions_count} />
                  <Mini label="Files" value={p.files_count} />
                  <Mini
                    label="Last active"
                    value={p.last_active_at ? fmtRelative(p.last_active_at) : "—"}
                  />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Mini({ label, value }) {
  return (
    <div className="rounded-md bg-white border border-[#e5e7eb] p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-[#6b7280]">{label}</div>
      <div className="text-sm text-[#111827] mt-0.5">{value}</div>
    </div>
  );
}
